// Reports screen (spec §8) — category donut, currency bar chart, photo locations map.
// Currency-combination rule enforced here too: the donut only ever shows either the
// explicitly-labeled ≈ approximate total, or a single raw currency — never a silent
// cross-currency sum (spec §19 rule 6).

import { iconPlain } from '../lib/icons';
import { fmt, categoryColor } from '../lib/format';
import { categoryTotals, currencyTotals } from '../lib/calc';
import { state, categoryName, categoryIconKey } from '../state';
import { initLeaflet } from '../lib/map';
import { renderAddButton, renderNav, renderOfflineBanner, emptyState } from './shared';

export function renderReports(): string {
  const mode = state.reportCurrencyMode;
  const catEntries = categoryTotals(state.expenses, mode);
  const catTotal = catEntries.reduce((s, [, v]) => s + v, 0) || 1;
  const donutCode = mode === 'approx' ? state.trip.baseCurrency : mode;

  let offset = 0;
  const R = 60;
  const C = 2 * Math.PI * R;
  const segs = catEntries
    .map(([catId, val], i) => {
      const frac = val / catTotal;
      const len = frac * C;
      const seg = `<circle cx="75" cy="75" r="${R}" fill="none" stroke="${categoryColor(i)}" stroke-width="16" stroke-dasharray="${len} ${
        C - len
      }" stroke-dashoffset="${-offset}" transform="rotate(-90 75 75)"/>`;
      offset += len;
      void catId;
      return seg;
    })
    .join('');

  const { totals: curTotals, counts: curCounts } = currencyTotals(state.expenses);
  const maxCur = Math.max(...Object.values(curTotals), 1);

  return `
  <div class="screen">
    ${renderOfflineBanner()}
    <div class="topbar"><h1>Reports</h1>${renderAddButton()}</div>

    <div class="filter-bar" role="group" aria-label="Report currency mode">
      <button class="filter-chip ${mode === 'approx' ? 'active' : ''}" onclick="App.setReportMode('approx')">All (≈ ${state.trip.baseCurrency})</button>
      ${state.currencies.map((c) => `<button class="filter-chip ${mode === c.code ? 'active' : ''}" onclick="App.setReportMode('${c.code}')">${c.code} only</button>`).join('')}
    </div>

    <div class="card">
      <div class="card-title"><h2>Spending by category</h2></div>
      ${
        catEntries.length
          ? `
      <div class="donut-wrap">
        <div class="donut">
          <svg width="150" height="150" viewBox="0 0 150 150" role="img" aria-label="Spending by category donut chart">${segs}</svg>
          <div class="donut-center"><div class="amt">${fmt(catTotal, donutCode)}</div><div class="lbl">${mode === 'approx' ? '≈ total' : 'total'}</div></div>
        </div>
        <div class="legend">${catEntries
          .slice(0, 5)
          .map(([catId, v], i) => `<div class="legend-row"><span class="legend-sw" style="background:${categoryColor(i)}"></span><span class="nm">${categoryName(catId)}</span><span class="val">${Math.round((v / catTotal) * 100)}%</span></div>`)
          .join('')}</div>
      </div>
      <div class="cat-list">${catEntries
        .map(
          ([catId, v], i) => `
        <div class="cat-row">
          <span class="nm">${iconPlain(categoryIconKey(catId))} ${categoryName(catId)}</span>
          <div class="cat-bar-bg"><div class="cat-bar-fill" style="width:${((v / catTotal) * 100).toFixed(0)}%;background:${categoryColor(i)}"></div></div>
          <span class="val">${fmt(v, donutCode)}</span>
        </div>`
        )
        .join('')}</div>
      ${
        mode === 'approx'
          ? `<div class="budget-note" style="margin-top:12px;">Values are ≈ converted to ${state.trip.baseCurrency} using fixed reference rates for comparison only. Switch to a single-currency filter above to see unconverted totals.</div>`
          : ''
      }
      `
          : emptyState('chart', 'No spending yet', 'Add expenses to see category breakdowns.')
      }
    </div>

    <div class="card">
      <div class="card-title"><h2>Spending by currency</h2></div>
      ${
        Object.keys(curTotals).length
          ? `<div class="bar-chart">${Object.entries(curTotals)
              .sort((a, b) => b[1] - a[1])
              .map(
                ([c, v]) => `
        <div class="bar-row">
          <div class="top"><span class="code">${c}</span><span class="tx">${curCounts[c]} transaction${curCounts[c] > 1 ? 's' : ''} · ${fmt(v, c)}</span></div>
          <div class="bar-bg"><div class="bar-fill" style="width:${((v / maxCur) * 100).toFixed(0)}%"></div></div>
        </div>`
              )
              .join('')}</div>`
          : emptyState('chart', 'No spending yet', 'Add expenses to see currency totals.')
      }
    </div>

    <div class="card">
      <div class="card-title"><h2>Photo locations</h2></div>
      ${state.photos.some((p) => p.lat != null) ? `<div id="leafletMapReport" role="img" aria-label="Map of trip photo locations"></div>` : emptyState('marker', 'No located photos yet', 'Photos with location will appear here.')}
    </div>
  </div>
  ${renderNav()}`;
}

export function mountReports(): void {
  if (state.photos.some((p) => p.lat != null)) {
    initLeaflet('leafletMapReport', state.photos, { isReport: true });
  }
}
